import { useMemo } from "react";
import settingsService, { ContactFormPublishMethod } from "api/settingsService";
import { ENotificationType, notification } from "utils/notification";
import { IContactFormWizardSettings, WizardSettingsPatch } from "components/ContactFormWizard/typings";
import CopyIcon from "assets/icons/copy.svg?react";
import { useTranslation } from "react-i18next";
import wizardStyles from "components/ContactFormWizard/styles.module.scss";
import styles from "./PublishLeftPanel.module.scss";

interface MethodRowProps {
  id: ContactFormPublishMethod;
  label: string;
  description: string;
  snippet: string;
  selected: boolean;
  onSelect: () => void;
  onCopy: () => void;
}

const MethodRow = ({ label, description, snippet, selected, onSelect, onCopy }: MethodRowProps) => {
  const { t } = useTranslation();
  return (
    <div
      className={`${styles.methodRow} ${selected ? styles.methodRowActive : ""}`}
      onClick={onSelect}
      role="button"
    >
      <div className={styles.methodLabel}>{label}</div>
      <div className={styles.methodDesc}>{description}</div>
      <div className={wizardStyles.copyBlock}>
        <div className={wizardStyles.code}>
          <span className={wizardStyles.codeText}>{snippet || "—"}</span>
          <button
            type="button"
            className={wizardStyles.codeCopyBtn}
            onClick={onCopy}
            aria-label={t("common.copy")}
            title={t("common.copy")}
            disabled={!snippet}
          >
            <CopyIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

interface Props {
  settings: IContactFormWizardSettings;
  patchSettingsRef: React.MutableRefObject<(patch: WizardSettingsPatch) => void>;
}

const SafeEnvelopeLeftPanel = ({ settings, patchSettingsRef }: Props) => {
  const { t } = useTranslation();

  const { data: formData } = settingsService.useGetForm(settings.WorkspaceId);
  const key = formData?.data?.[0]?.Key;
  const base = import.meta.env.VITE_APP_FORM_SITE;
  const link = useMemo(() => (key ? `${base}?id=${key}` : ""), [key, base]);

  const copyToClipboard = async (text: string) => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    notification({
      type: ENotificationType.INFO,
      title: t("settings.copiedToClipboard"),
      text: t("settings.embedCodeCopied"),
    });
  };

  return (
    <div className={styles.root}>
      <div className={styles.hint}>
        <p>
          Bezpieczna koperta to kolejny sposób nadawania przesyłek szyfrowanych.
        </p>
        <p>
          Ktoś chce Ci przesłać wrażliwe dokumenty? Wyślij do niego pustą bezpieczną kopertę.
          On wklei do niej pliki, które chce wysłać do Ciebie, i zwrotnie odeśle kopertę
          do Ciebie. Proste? Proste!
        </p>
        <p>
          Pamiętaj! Bezpieczna koperta to narzędzie do wielokrotnego użycia. Wystarczy za każdym
          razem wysyłać jeden i ten sam link.
        </p>
      </div>
      <div className={styles.methods}>
        <MethodRow
          id="link"
          label="Bezpieczna Koperta"
          description="Tak wygląda link uruchamiający mechanizm Bezpieczna Koperta"
          snippet={link}
          selected={settings.PublishMethod === "link"}
          onSelect={() => patchSettingsRef.current({ PublishMethod: "link" })}
          onCopy={() => copyToClipboard(link)}
        />
      </div>
    </div>
  );
};

export default SafeEnvelopeLeftPanel;
